import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

// Commissione a soglia: 10% sopra €45 di prezzo lavoratore, altrimenti €4,50 fissi
// — deve restare identica alla logica di confirm-booking-payment (Edge Function),
// altrimenti l'anteprima qui mostrata non corrisponde a quanto viene davvero addebitato.
const COMMISSION_RATE = 0.10;
const COMMISSION_THRESHOLD_EURO = 45;
const COMMISSION_FIXED_EURO = 4.50;

const timeSlotLabels = {
  mattina: '🌅 Mattina (8:00 - 13:00)',
  pomeriggio: '☀️ Pomeriggio (13:00 - 18:00)',
  sera: '🌙 Sera (18:00 - 22:00)',
  tutto_il_giorno: '🕐 Tutto il giorno (8:00 - 18:00)',
  da_concordare: '💬 Da concordare',
};

function OfferCard({ msg, isMe, isApplicant, applicantId, employerId, application, session, conversationId, onUpdate, formatTime }) {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [sendingCounter, setSendingCounter] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterPriceType, setCounterPriceType] = useState('fisso');
  const [counterHours, setCounterHours] = useState('');

  const offerData = msg.offer_data || {};
  const isOra = offerData.price_type === 'ora';
  const hours = offerData.hours;
  const totalAmount = isOra && hours
    ? (parseFloat(offerData.price) * parseFloat(hours)).toFixed(2)
    : offerData.price;

  const priceDisplay = isOra
    ? (hours ? `${offerData.price}/h × ${hours}h = €${totalAmount}` : `${offerData.price}/h`)
    : offerData.price;

  const workerAmountNum = parseFloat(totalAmount);
  const commissionAmount = (workerAmountNum >= COMMISSION_THRESHOLD_EURO
    ? workerAmountNum * COMMISSION_RATE
    : COMMISSION_FIXED_EURO
  ).toFixed(2);
  const totalWithCommission = (workerAmountNum + parseFloat(commissionAmount)).toFixed(2);

  const isCancelled = application?.status === 'cancellata';
  const isAccepted = application?.status === 'accettata';
  const isRejected = application?.status === 'rifiutata';
  const isResolved = isAccepted || isCancelled || isRejected;

  // Flag persistente sul singolo messaggio: questa specifica offerta ha già ricevuto
  // una risposta (accettata dal lavoratore e inoltrata, oppure superata da un rilancio).
  const isHandled = !!offerData.responded;
  const responseType = offerData.response_type;

  const wasThisOfferAccepted = application?.proposed_price != null
    && parseFloat(application.proposed_price) === parseFloat(offerData.price);

  // SOLO il committente può accettare e pagare, e SOLO su un'offerta che ha ricevuto,
  // mai su una che ha scritto lui (niente self-accept: rischierebbe di far pagare
  // un'offerta appena inviata prima ancora che il lavoratore l'abbia vista o confermata).
  // Se vuole finalizzare una sua proposta dopo un ok verbale, il lavoratore deve prima
  // cliccare "Accetta" per re-inviarla, e solo quella copia diventa pagabile.
  const isEmployerViewer = !isApplicant;
  const canAcceptAndPay = isEmployerViewer && !isMe && !isResolved && !isHandled;

  // Il lavoratore, quando riceve un'offerta del committente, non paga: "accetta" per lui
  // significa rimandare la stessa offerta come nuovo messaggio, così il committente
  // la vede e può finalizzarla lui stesso con il pagamento reale.
  const canWorkerAccept = !isEmployerViewer && !isMe && !isResolved && !isHandled;

  // Rilancia/Rifiuta hanno senso solo per chi sta valutando un'offerta altrui, ancora aperta.
  const canRespond = !isResolved && !isMe && !isHandled;

  const isThisOfferAccepted = !isCancelled && isAccepted && wasThisOfferAccepted;
  const wasSuperseded = isAccepted && !wasThisOfferAccepted;

  const isCounterOra = counterPriceType === 'ora';
  const counterIsValid = counterPrice && (!isCounterOra || (counterHours && parseFloat(counterHours) > 0));
  const counterTotal = isCounterOra && counterPrice && counterHours
    ? (parseFloat(counterPrice) * parseFloat(counterHours)).toFixed(2)
    : null;
  const counterPriceLabel = isCounterOra
    ? (counterTotal ? `${counterPrice}/h × ${counterHours}h = €${counterTotal}` : `${counterPrice}/h`)
    : counterPrice;

  // Chi ha scritto questo messaggio va avvisato della risposta (se non sono io stesso)
  const notifyUserId = msg.sender_id !== session.user.id ? msg.sender_id : null;

  const markAsHandled = async (type) => {
    await supabase.from('messages')
      .update({ offer_data: { ...offerData, responded: true, response_type: type } })
      .eq('id', msg.id);
  };

  const handleAcceptOffer = async () => {
    if (accepting) return;
    setAccepting(true);

    try {
      if (!employerId || !applicantId) {
        alert('Errore: impossibile determinare i ruoli della trattativa. Ricarica la pagina e riprova.');
        return;
      }

      // 1. Verifica che il committente (chi paga) abbia un metodo di pagamento valido
      const { data: customerRow } = await supabase
        .from('stripe_customers')
        .select('payment_method_validated')
        .eq('user_id', employerId)
        .maybeSingle();

      if (!customerRow?.payment_method_validated) {
        alert('Devi impostare un metodo di pagamento valido prima di poter accettare un\'offerta.');
        navigate('/profilo');
        return;
      }

      // 1b. Verifica che il lavoratore abbia completato l'onboarding Stripe (può ricevere pagamenti)
      // Usa una funzione RPC SECURITY DEFINER perché RLS su stripe_accounts limita la SELECT
      // diretta alla propria riga: il committente deve poter controllare lo stato del lavoratore.
      const { data: workerOnboardingStatus } = await supabase
        .rpc('get_worker_onboarding_status', { worker_id: applicantId });

      if (workerOnboardingStatus !== 'completato') {
        alert('Il lavoratore non ha ancora completato la configurazione per ricevere pagamenti. Non è possibile procedere.');
        return;
      }

      const price = parseFloat(offerData.price);
      const amountToBlock = isOra ? parseFloat(totalAmount) : price;

      // 2. Chiama la Edge Function che blocca i fondi (off-session, con carta già salvata)
      const { data: paymentResult, error: paymentError } = await supabase.functions.invoke('confirm-booking-payment', {
        body: {
          client_user_id: employerId,
          worker_user_id: applicantId,
          amount_worker_euro: amountToBlock,
          application_id: parseInt(conversationId.replace('app_', '')),
        },
      });

      if (paymentError || paymentResult?.error) {
        const errCode = paymentResult?.error;
        if (errCode === 'no_payment_method') {
          alert('Devi impostare un metodo di pagamento valido prima di poter accettare un\'offerta.');
          navigate('/profilo');
        } else if (errCode === 'authentication_required') {
          alert(paymentResult?.message || 'La tua banca richiede una conferma aggiuntiva. Riprova dal tuo profilo.');
        } else {
          alert('Errore nel blocco del pagamento. Riprova.');
        }
        return;
      }

      // Traccia "pagamento_bloccato" — il pagamento è stato bloccato con successo sul balance Jobbetty
      trackEvent('pagamento_bloccato', {
        conversation_id: conversationId,
        application_id: application.id,
        price,
        price_type: offerData.price_type,
        hours: isOra ? hours : null,
        amount_total: paymentResult?.amount_total,
        source: 'offer',
      });

      // 3. Pagamento bloccato con successo: procedi con l'accettazione dell'offerta
      const updateData = isOra
        ? { status: 'accettata', proposed_price: price, agreed_hours: parseFloat(hours) }
        : { status: 'accettata', proposed_price: price };

      await supabase.from('applications').update(updateData).eq('id', application.id);

      const totalPaid = paymentResult?.amount_total;
      const confirmText = isOra
        ? `✅ Offerta accettata! Accordo a €${offerData.price}/h × ${hours}h = €${totalAmount} — pagato €${totalPaid?.toFixed(2)}`
        : `✅ Offerta accettata! Accordo confermato a €${price} — pagato €${totalPaid?.toFixed(2)}`;

      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: confirmText,
        read: false,
        type: 'text',
      }]);

      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: isOra
          ? `🎉 Accordo raggiunto a €${offerData.price}/h per ${hours}h (€${totalAmount}) — al termine confermate entrambi il completamento`
          : `🎉 Accordo raggiunto per €${price}!`,
        read: false,
        type: 'completion_request',
        offer_data: { price, price_type: offerData.price_type, hours: isOra ? hours : null },
      }]);

      // Traccia "offerta_accettata" — percorso: accettazione di un'offerta formale in chat
      trackEvent('offerta_accettata', {
        conversation_id: conversationId,
        application_id: application.id,
        price,
        price_type: offerData.price_type,
        hours: isOra ? hours : null,
        amount_total: totalPaid,
        source: 'offer',
      });

      onUpdate({
        status: 'accettata',
        proposed_price: price,
        ...(isOra ? { agreed_hours: parseFloat(hours) } : {}),
      });

      if (notifyUserId) {
        sendPushNotification({
          userId: notifyUserId,
          title: '🎉 Offerta accettata!',
          body: `La tua offerta da €${priceDisplay} è stata accettata — pagamento bloccato`,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setAccepting(false);
    }
  };

  // Il lavoratore "accetta" un'offerta del committente: non paga nulla, semplicemente
  // rimanda la stessa offerta come nuovo messaggio, così il committente la vede e
  // può finalizzarla lui con il pagamento reale. Il messaggio originale viene marcato
  // come "gestito" per non poter essere ri-accettato/rilanciato/rifiutato di nuovo.
  const handleWorkerAccept = async () => {
    if (forwarding) return;
    setForwarding(true);
    try {
      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: `💼 Offerta formale — €${priceDisplay}`,
        read: false,
        type: 'offer',
        offer_data: {
          price: offerData.price,
          price_type: offerData.price_type,
          hours: offerData.hours || null,
          dates: offerData.dates || [],
          time_slot: offerData.time_slot || null,
        },
      }]);

      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: '✅ Ho accettato l\'offerta — in attesa che il committente confermi il pagamento.',
        read: false,
        type: 'text',
      });

      await markAsHandled('accepted_forwarded');

      if (notifyUserId) {
        sendPushNotification({
          userId: notifyUserId,
          title: '✅ Offerta accettata dal lavoratore',
          body: `Conferma il pagamento per finalizzare l'accordo da €${priceDisplay}`,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setForwarding(false);
    }
  };

  const handleReject = async () => {
    if (rejecting) return;
    setRejecting(true);
    try {
      await supabase.from('applications').update({ status: 'rifiutata' }).eq('id', application.id);
      onUpdate({ status: 'rifiutata' });
      await markAsHandled('rejected');

      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: '❌ Offerta rifiutata.',
        read: false,
        type: 'text',
      });

      if (notifyUserId) {
        sendPushNotification({
          userId: notifyUserId,
          title: '❌ Offerta rifiutata',
          body: `La tua offerta da €${priceDisplay} è stata rifiutata`,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setRejecting(false);
    }
  };

  const handleSendCounter = async () => {
    if (sendingCounter || !counterIsValid) return;
    setSendingCounter(true);
    try {
      await supabase.from('applications').update({ status: 'controproposta' }).eq('id', application.id);
      onUpdate({ status: 'controproposta' });

      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: `💼 Rilancio — €${counterPriceLabel}`,
        read: false,
        type: 'offer',
        offer_data: {
          price: counterPrice,
          price_type: counterPriceType,
          hours: isCounterOra ? counterHours : null,
          dates: offerData.dates || [],
          time_slot: offerData.time_slot || null,
        },
      }]);

      await markAsHandled('countered');

      if (notifyUserId) {
        sendPushNotification({
          userId: notifyUserId,
          title: '💼 Nuovo rilancio',
          body: `Hai ricevuto un rilancio da €${counterPriceLabel}`,
          url: `/chat/${conversationId}`,
        });
      }

      setShowCounterForm(false);
      setCounterPrice('');
      setCounterHours('');
      setCounterPriceType('fisso');
    } finally {
      setSendingCounter(false);
    }
  };

  const handledLabel = () => {
    if (responseType === 'accepted_forwarded') return '✅ Accettata — inoltrata per il pagamento';
    if (responseType === 'countered') return '🔄 Superata da un rilancio';
    return null;
  };

  return (
    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: theme.spacing.md }}>
      <div style={{
        maxWidth: '85%', borderRadius: theme.radius.lg,
        border: `2px solid ${theme.colors.primary}`,
        background: isMe ? theme.colors.primaryLight : theme.colors.white,
        overflow: 'hidden',
        boxShadow: theme.shadow.popup
      }}>
        <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '8px 14px', fontSize: theme.font.size.base, fontWeight: 700 }}>
          💼 Offerta formale
        </div>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: isOra && hours ? 18 : 24, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.sm }}>
            €{priceDisplay}
          </div>
          {(canAcceptAndPay || wasThisOfferAccepted) && (
            <div style={{ background: '#f7f7f7', borderRadius: theme.radius.sm, padding: '8px 10px', marginBottom: theme.spacing.sm, fontSize: theme.font.size.sm, color: theme.colors.textSecondary }}>
              💳 {wasThisOfferAccepted ? 'Totale pagato' : 'Totale da pagare'}: <strong style={{ color: theme.colors.primary }}>€{totalWithCommission}</strong>
              <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                (€{totalAmount} al lavoratore + €{commissionAmount} commissione Jobbetty)
              </div>
            </div>
          )}
          {offerData.dates?.length > 0 && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 4 }}>
              📅 {offerData.dates.map(d =>
                new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
              ).join(', ')}
            </div>
          )}
          {offerData.time_slot && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>
              🕐 {timeSlotLabels[offerData.time_slot]}
            </div>
          )}

          {showCounterForm && canRespond && (
            <div style={{ background: '#f0faf0', borderRadius: theme.radius.sm, padding: 10, marginBottom: theme.spacing.sm }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 70 }}>
                  <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: theme.colors.primary, fontWeight: 700, fontSize: theme.font.size.sm }}>€</span>
                  <input type="number" min="0" step="0.01" value={counterPrice} onChange={e => setCounterPrice(e.target.value)}
                    placeholder="Prezzo"
                    style={{ width: '100%', padding: '7px 8px 7px 20px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.sm, boxSizing: 'border-box' }} />
                </div>
                <select value={counterPriceType} onChange={e => { setCounterPriceType(e.target.value); if (e.target.value !== 'ora') setCounterHours(''); }}
                  style={{ padding: '7px 8px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.sm, background: theme.colors.white }}>
                  <option value="fisso">fisso</option>
                  <option value="ora">/ ora</option>
                </select>
                {isCounterOra && (
                  <input type="number" min="0.5" step="0.5" value={counterHours} onChange={e => setCounterHours(e.target.value)}
                    placeholder="Ore"
                    style={{ width: 56, padding: '7px 6px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.sm, boxSizing: 'border-box' }} />
                )}
              </div>
              {isCounterOra && counterPrice && counterHours && (
                <div style={{ marginTop: 6, fontSize: theme.font.size.sm, color: theme.colors.primary, fontWeight: 600 }}>
                  Totale: €{counterPrice} × {counterHours}h = €{counterTotal}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={() => setShowCounterForm(false)} disabled={sendingCounter}
                  style={{ flex: 1, background: theme.colors.white, color: theme.colors.textMuted, border: `1px solid ${theme.colors.borderInput}`, borderRadius: theme.radius.sm, padding: 8, fontSize: theme.font.size.sm, cursor: 'pointer' }}>
                  Annulla
                </button>
                <button onClick={handleSendCounter} disabled={sendingCounter || !counterIsValid}
                  style={{ flex: 2, background: counterIsValid ? theme.colors.primary : '#ccc', color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 8, fontSize: theme.font.size.sm, fontWeight: 700, cursor: counterIsValid ? 'pointer' : 'not-allowed' }}>
                  {sendingCounter ? '⏳ Invio...' : 'Invia rilancio'}
                </button>
              </div>
            </div>
          )}

          {canAcceptAndPay && !showCounterForm && (
            <button
              onClick={handleAcceptOffer}
              disabled={accepting}
              style={{ width: '100%', background: accepting ? '#ccc' : theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.md, fontWeight: 700, cursor: accepting ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {accepting ? '⏳ Blocco pagamento in corso...' : `✅ Accetta e paga €${totalWithCommission}`}
            </button>
          )}

          {canWorkerAccept && !showCounterForm && (
            <button
              onClick={handleWorkerAccept}
              disabled={forwarding}
              style={{ width: '100%', background: forwarding ? '#ccc' : theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.md, fontWeight: 700, cursor: forwarding ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {forwarding ? '⏳ Invio...' : '✅ Accetta'}
            </button>
          )}

          {canRespond && !showCounterForm && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={() => setShowCounterForm(true)} disabled={rejecting || forwarding}
                style={{ flex: 1, background: theme.colors.infoBg, color: theme.colors.info, border: 'none', borderRadius: theme.radius.sm, padding: 8, fontSize: theme.font.size.sm, fontWeight: 700, cursor: 'pointer' }}>
                🔄 Rilancia
              </button>
              <button onClick={handleReject} disabled={rejecting || forwarding}
                style={{ flex: 1, background: theme.colors.white, color: theme.colors.errorDark, border: `1px solid ${theme.colors.errorDark}`, borderRadius: theme.radius.sm, padding: 8, fontSize: theme.font.size.sm, cursor: 'pointer' }}>
                {rejecting ? '⏳...' : '❌ Rifiuta'}
              </button>
            </div>
          )}

          {isCancelled && wasThisOfferAccepted && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
              🚫 Prenotazione cancellata
            </div>
          )}
          {isThisOfferAccepted && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.primary, fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
              ✅ Offerta accettata
            </div>
          )}
          {wasSuperseded && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, textAlign: 'center', marginTop: 4 }}>
              È stata accettata un'altra offerta
            </div>
          )}
          {isRejected && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.errorDark, fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
              ❌ Offerta rifiutata
            </div>
          )}
          {isHandled && !isResolved && handledLabel() && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
              {handledLabel()}
            </div>
          )}
          {isMe && !isResolved && !isHandled && (
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, textAlign: 'center', marginTop: 4 }}>
              In attesa della risposta del lavoratore...
            </div>
          )}

          <div style={{ fontSize: 10, color: theme.colors.textMuted, textAlign: 'right', marginTop: 6 }}>
            {formatTime(msg.created_at)}
            {isMe && <span style={{ marginLeft: 4 }}>{msg.read ? '✓✓' : '✓'}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfferCard;